import { Card } from "@/components/layout";
import {
  MetricKpiTile,
  SectionHeading,
  WebVitalTile,
  panelErrorTitle,
} from "@/components/dashboard";
import { usePerformance } from "@/hooks/usePerformance";
import {
  VITAL_THRESHOLDS,
  classifyVital,
  pickErrorRateTone,
} from "@/lib/thresholds";
import type { TimeRange } from "@/lib/time-range";
import type { TimeSeriesPoint, WebVital, WebVitalMetric } from "@/lib/validation";

interface PerformanceSectionProps {
  range: TimeRange;
}

interface VitalConfig {
  metric: WebVitalMetric;
  label: string;
  hint: string;
}

const BFF_LATENCY_HINTS: Record<"p50" | "p95" | "p99", string> = {
  p50: "Median backend response time. Half of requests are faster than this.",
  p95: "95th-percentile backend response time. Only the slowest 5% of requests take longer than this.",
  p99: "99th-percentile backend response time. Only the slowest 1% of requests take longer than this — useful for spotting tail-latency issues.",
};

const VITAL_CONFIG: readonly VitalConfig[] = [
  {
    metric: "web_vitals_lcp",
    label: "LCP",
    hint: "Largest Contentful Paint — how long the main hero content takes to appear. Aim for under 2.5 seconds.",
  },
  {
    metric: "web_vitals_inp",
    label: "INP",
    hint: "Interaction to Next Paint — how quickly the page responds when a user clicks or types. Aim for under 200 ms.",
  },
  {
    metric: "web_vitals_cls",
    label: "CLS",
    hint: "Cumulative Layout Shift — how much the page jumps around while it loads. Aim for under 0.1.",
  },
  {
    metric: "web_vitals_ttfb",
    label: "TTFB",
    hint: "Time to First Byte — how long the server takes to start sending the page. Aim for under 0.8 seconds.",
  },
] as const;

function lastValue(points: TimeSeriesPoint[]): number | null {
  if (points.length === 0) return null;
  const sorted = [...points].sort(
    (a, b) => new Date(a.bucket).getTime() - new Date(b.bucket).getTime(),
  );
  const last = sorted[sorted.length - 1]?.value;
  return Number.isFinite(last) ? last : null;
}

function findVital(
  vitals: WebVital[] | undefined,
  metric: WebVitalMetric,
): WebVital | undefined {
  return vitals?.find((v) => v.metric === metric);
}

export function PerformanceSection({ range }: PerformanceSectionProps) {
  const perf = usePerformance(range);
  const p = perf.data;
  const err = perf.error ? panelErrorTitle(perf.error) : undefined;

  const errorRate = p ? lastValue(p.bff.errorRate) : null;

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="Performance & Reliability"
        frs={["FR-08"]}
        source="log-analytics"
        description="Core Web Vitals at p75 plus BFF request latency and 5xx rate."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {VITAL_CONFIG.map((cfg) => {
          const vital = findVital(p?.webVitals, cfg.metric);
          const value = vital ? lastValue(vital.p75) : null;
          const { status, markerPct } = classifyVital(cfg.metric, value);
          const threshold = VITAL_THRESHOLDS[cfg.metric];
          return (
            <WebVitalTile
              key={cfg.metric}
              label={cfg.label}
              value={value}
              unit={threshold.unit}
              fractionDigits={threshold.fractionDigits}
              status={status}
              markerPct={markerPct}
              hint={cfg.hint}
              isLoading={perf.isLoading}
              errorTitle={err}
            />
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="BFF latency (p50 / p95 / p99)" className="lg:col-span-2">
          <div className="grid grid-cols-3 gap-4">
            {(["p50", "p95", "p99"] as const).map((key) => {
              const value = p ? lastValue(p.bff.latency[key]) : null;
              return (
                <MetricKpiTile
                  key={key}
                  label={key.toUpperCase()}
                  value={
                    value === null || !Number.isFinite(value)
                      ? "—"
                      : `${Math.round(value)} ms`
                  }
                  tone={key === "p50" ? "emerald" : key === "p95" ? "amber" : "red"}
                  size="sm"
                  hint={BFF_LATENCY_HINTS[key]}
                  isLoading={perf.isLoading}
                  errorTitle={err}
                />
              );
            })}
          </div>
        </Card>

        <MetricKpiTile
          label="BFF 5xx rate"
          value={
            errorRate === null || !Number.isFinite(errorRate)
              ? "—"
              : `${errorRate.toFixed(2)}%`
          }
          delta="% of RequestData with code ≥ 500"
          tone={pickErrorRateTone(errorRate, { good: 1, warn: 5 })}
          hint="Share of recent backend requests that returned a server error. Stays close to 0% when the API is healthy."
          isLoading={perf.isLoading}
          errorTitle={err}
        />
      </div>
    </section>
  );
}
